import axios from 'axios';
import { saveAs } from 'file-saver';
import React from 'react';
import { toast } from 'sonner';

import { Box } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import withWidth from '@material-ui/core/withWidth';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import GetAppIcon from '@material-ui/icons/GetApp';

import Button from 'creative-components/CustomButtons/Button';
import ReactTable from 'creative-components/ReactTable/ReactTable';

import BadgedContacts from 'components/BadgedContacts';
import CampaignLeadsBulkEditModal from 'components/CampaignLeadsBulkEditModal';
import EditCampaignEventOverrideModal from 'components/CampaignProspectsTable/EditCampaignEventOverrideModal';
import QRCodeIcon from 'components/CustomIcons/QRCodeIcon';

import { exportCampaign } from 'utils/api';
import {
  getAgentLandingPageSampleUrlFromType,
  getEventDataOwnerInformation,
  stringifyEventSiteAddressObject,
  tableFilterOwnerInfo,
  tableFilterPhoneNumbers,
  tableSortEmails,
  tableSortOwnerInfo,
  tableSortPhoneNumbers,
} from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    overflowX: 'auto',
    overflowY: 'auto',
  },
}));

const CampaignProspectsTable = ({ campaign, width }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [infoModal, setInfoModal] = React.useState(null);
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = React.useState(false);

  const [data, setData] = React.useState([]);
  const [columnsToRender, setColumnsToRender] = React.useState([]);

  React.useEffect(() => {
    // The # of columns we render depends on the screen width
    let columns = [
      {
        Header: 'Farming Enabled',
        accessor: 'farmingEnabled',
        Cell: ({ value }) => (value ? <CheckIcon htmlColor={theme.palette.success.main} /> : <CloseIcon htmlColor={theme.palette.error.main} />),
        disableFilters: true,
        sortType: (rowA, rowB) => {
          const a = rowA.values.farmingEnabled ? 1 : 0; // Convert true/false to 1/0
          const b = rowB.values.farmingEnabled ? 1 : 0;
          return a - b; // Sort in ascending order (false -> true)
        },
      },
      {
        Header: 'APN',
        accessor: 'apn',
      },
      {
        Header: 'Site Address',
        accessor: 'siteAddress',
      },
      {
        Header: 'Owner Name & Address',
        accessor: 'ownerInfo',
        filter: tableFilterOwnerInfo,
        sortType: tableSortOwnerInfo,
      },
      {
        Header: 'Phone Numbers',
        accessor: 'phoneNumbers',
        filter: tableFilterPhoneNumbers,
        sortType: tableSortPhoneNumbers,
        Cell: ({ value }) => <BadgedContacts main={value[0]} alternates={value.length > 1 ? value.slice(1) : []} />,
      },
      {
        Header: 'Emails',
        accessor: 'emails',
        sortType: tableSortEmails,
        Cell: ({ value }) => <BadgedContacts main={value[0]} alternates={value.length > 1 ? value.slice(1) : []} />,
      },
    ];

    const persistentColumns = [
      {
        Header: 'Actions',
        accessor: 'actions',
      },
    ];

    /* switch (width) {
      case 'xs':
      case 'sm':
        // First 2 columns
        columns = [...columns.splice(0, 2), ...persistentColumns];
        break;
      case 'md':
        // First 4 columns
        columns = [...columns.splice(0, 4), ...persistentColumns];
        break;
      default:
        // All columns
        columns = [...columns, ...persistentColumns];
        break;
    } */

    columns = [...columns, ...persistentColumns];

    setColumnsToRender(columns);
  }, [width]);

  React.useEffect(() => {
    if (campaign === null) return;

    setData(campaign.subscribedEvents.map((eventData) => {
      const {
        _id, apn, ownerContact, siteAddress,
      } = eventData;
      const existingEventOverride = campaign.eventOverrides.find((o) => o.event === _id);

      const {
        name, phoneNumber, email, ownerAddress, alternateEmails, alternatePhoneNumbers,
      } = getEventDataOwnerInformation(existingEventOverride, eventData);

      return ({
        farmingEnabled: !(existingEventOverride && existingEventOverride.farmingStopped),
        apn,
        siteAddress: stringifyEventSiteAddressObject(siteAddress),
        ownerName: name, // For ownerInfo filter function
        ownerAddress, // For ownerInfo filter function
        ownerInfo: (
          <>
            <div style={{ fontWeight: 600, marginBottom: '5px' }}>{name}</div>
            {ownerAddress}
          </>
        ),
        phoneNumbers: [phoneNumber, ...(alternatePhoneNumbers ?? [])],
        emails: [email, ...(alternateEmails ?? [])],
        actions: (
          <div>
            <Tooltip
              title={(
                <>
                  View QR Code
                  <br />
                  Landing Page
                </>
              )}
              placement="top"
            >
              <Button
                justIcon
                round
                simple
                onClick={() => window.open(getAgentLandingPageSampleUrlFromType(campaign.agentLandingPageType, _id), '_blank', 'noopener,noreferrer')}
                color="dark"
              >
                <QRCodeIcon />
              </Button>
            </Tooltip>
            <Tooltip
              title="Edit"
              placement="top"
            >
              <Button
                justIcon
                round
                simple
                onClick={() => {
                  setInfoModal(() => (
                    <EditCampaignEventOverrideModal
                      campaignId={campaign._id}
                      monthlyRatePerApn={campaign.costs?.monthlyRatePerApn}
                      isAnnualBilling={campaign.costs?.isAnnualBilling}
                      eventId={_id}
                      defaultOwnerContactInfo={ownerContact}
                      existingEventOverride={existingEventOverride}
                      onCancel={() => setInfoModal(null)}
                    />
                  ));
                }}
                color={existingEventOverride ? 'primary' : 'dark'}
              >
                <EditIcon />
              </Button>
            </Tooltip>
          </div>
        ),
      });
    }));
  }, [campaign]);

  const handleExportToCsv = async (bulkEdit) => {
    toast.success('We are gathering your data to export. Your download will begin shortly.');

    const { csvFileUrl } = await exportCampaign(campaign._id, bulkEdit);

    const { data: csvData } = await axios.get(csvFileUrl, { responseType: 'blob' });
    saveAs(csvData, `campaign-${campaign._id}-prospects-export.csv`);
  };

  return (
    <>
      {infoModal}

      {isBulkEditModalOpen && (
        <CampaignLeadsBulkEditModal
          campaignId={campaign._id}
          handleExportToCsv={() => handleExportToCsv(true)}
          onClose={() => setIsBulkEditModalOpen(false)}
        />
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Button
          round
          color="dark"
          onClick={() => setIsBulkEditModalOpen(true)}
        >
          <EditIcon />
          Bulk Edit
        </Button>

        <Button
          round
          color="primary"
          onClick={() => handleExportToCsv(false)}
        >
          <GetAppIcon />
          Export
        </Button>
      </Box>

      <div className={classes.tableContainer}>
        <ReactTable
          columns={columnsToRender}
          data={data}
          autoResetFilters={false}
        />
      </div>
    </>
  );
};

export default withWidth()(CampaignProspectsTable);
